import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, getBaseCurrency, fmtMoney, parseNum } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CircleNotch, Plus, Trash } from "@phosphor-icons/react";
import { DateField } from "@/components/ui/date-field";

const CURRENCIES = ["CLP", "USD", "EUR"];
const NEW = "__new__";

const EMPTY = {
  numero_lote: "",
  fecha_importacion: "",
  fecha_vencimiento: "",
  cantidad_inicial: "",
  costo_unitario: "",
  moneda: "",
  tipo_cambio: "",
  proveedor_id: "",
  numero_factura: "",
  factura_neto: "",
  factura_iva: "",
  factura_vencimiento: "",
  estado_pago: "pendiente",
  monto_abonado: "",
};

const emptyLine = () => ({
  producto_id: "",
  nuevo: false,
  codigo: "",
  tipo: "",
  nombre: "",
  cantidad: "",
  costo_unitario: "",
  fecha_vencimiento: "",
});

export default function LoteForm({ open, onOpenChange, lote, productoId, onSaved }) {
  const isEdit = Boolean(lote);
  const baseCurrency = getBaseCurrency();
  const [form, setForm] = useState({ ...EMPTY, moneda: baseCurrency });
  const [lines, setLines] = useState([emptyLine()]);
  const [productos, setProductos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [compra, setCompra] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [provInline, setProvInline] = useState(false);
  const [nuevoProv, setNuevoProv] = useState({ nombre: "", email: "", telefono: "", condiciones: "" });
  const [savingProv, setSavingProv] = useState(false);

  const crearProveedor = async () => {
    if (!nuevoProv.nombre.trim()) {
      toast.error("El nombre del proveedor es obligatorio");
      return;
    }
    setSavingProv(true);
    try {
      const r = await api.post("/proveedores", nuevoProv);
      const creado = r.data;
      setProveedores((prev) =>
        [...prev, creado].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "")));
      setForm((f) => ({ ...f, proveedor_id: creado.id }));
      setNuevoProv({ nombre: "", email: "", telefono: "", condiciones: "" });
      setProvInline(false);
      toast.success("Proveedor agregado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo crear el proveedor");
    } finally {
      setSavingProv(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setError("");
    setCompra(null);
    setProvInline(false);
    setNuevoProv({ nombre: "", email: "", telefono: "", condiciones: "" });
    api.get("/proveedores").then((r) => setProveedores(r.data));
    if (lote) {
      setForm({
        numero_lote: lote.numero_lote,
        fecha_importacion: lote.fecha_importacion,
        fecha_vencimiento: lote.fecha_vencimiento || "",
        cantidad_inicial: lote.cantidad_inicial,
        costo_unitario: lote.costo_unitario_origen ?? lote.costo_unitario,
        moneda: lote.moneda || baseCurrency,
        tipo_cambio: lote.moneda && lote.moneda !== baseCurrency ? lote.tipo_cambio : "",
        proveedor_id: lote.proveedor_id || "",
        numero_factura: lote.numero_factura || "",
        factura_neto: lote.factura_neto ?? "",
        factura_iva: lote.factura_iva ?? "",
        factura_vencimiento: lote.factura_vencimiento || "",
        estado_pago: lote.estado_pago || "pendiente",
        monto_abonado: lote.estado_pago === "abono" ? (lote.monto_abonado ?? "") : "",
      });
    } else {
      const d = new Date();
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      setForm({ ...EMPTY, moneda: baseCurrency, fecha_importacion: today });
      setLines(productoId ? [{ ...emptyLine(), producto_id: productoId }] : [emptyLine()]);
      api.get("/lotes/sugerir-numero").then((r) => setForm((f) => ({ ...f, numero_lote: r.data.numero_lote })));
      api.get("/productos").then((r) => setProductos(r.data));
    }
  }, [open, lote, productoId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Detecta si el Nº de lote ya existe (para avisar que se agregará a esa compra).
  const checkCompra = async (numero) => {
    if (isEdit || !numero) {
      setCompra(null);
      return;
    }
    try {
      const { data } = await api.get(`/compra-info?numero_lote=${encodeURIComponent(numero)}`);
      setCompra(data.exists ? data : null);
    } catch {
      setCompra(null);
    }
  };
  const fmtFecha = (s) => (s ? s.split("-").reverse().join("/") : "—");

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, j) => j !== i) : ls));
  const onProductoSelect = (i, v) => {
    if (v === NEW) setLine(i, "nuevo", true);
    else setLines((ls) => ls.map((l, j) => (j === i ? { ...l, nuevo: false, producto_id: v } : l)));
  };

  const esExtranjera = form.moneda && form.moneda !== baseCurrency;
  const tc = parseNum(form.tipo_cambio) || 0;

  // Factura (de la compra completa)
  const fNeto = parseNum(form.factura_neto) || 0;
  const fIva = parseNum(form.factura_iva) || 0;
  const fBruto = fNeto + fIva;
  const fAbonado = parseNum(form.monto_abonado) || 0;
  const fSaldo =
    form.estado_pago === "pagado" ? 0 : form.estado_pago === "abono" ? Math.max(0, fBruto - fAbonado) : fBruto;

  const onNetoChange = (e) => {
    const v = e.target.value;
    const neto = parseNum(v);
    setForm((f) => ({ ...f, factura_neto: v, factura_iva: v && !isNaN(neto) ? String(Math.round(neto * 0.19)) : "" }));
  };

  const facturaPayload = () => ({
    numero_factura: form.numero_factura || null,
    factura_neto: form.factura_neto !== "" ? fNeto : null,
    factura_iva: form.factura_iva !== "" ? fIva : null,
    factura_bruto: form.factura_neto !== "" || form.factura_iva !== "" ? fBruto : null,
    factura_vencimiento: form.factura_vencimiento || null,
    estado_pago: form.estado_pago,
    monto_abonado: form.estado_pago === "abono" ? fAbonado : null,
  });

  const commonPayload = () => ({
    numero_lote: form.numero_lote,
    fecha_importacion: form.fecha_importacion,
    moneda: form.moneda || baseCurrency,
    tipo_cambio: esExtranjera ? tc : null,
    proveedor_id: form.proveedor_id,
    ...facturaPayload(),
  });

  // Muestra el error de forma visible (toast + recuadro en el formulario).
  const fail = (msg) => {
    setError(msg);
    toast.error(msg);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.fecha_importacion) {
      fail("Falta la fecha de importación / compra. Selecciónala en el calendario.");
      return;
    }
    if (!form.proveedor_id && !compra) {
      fail("Falta el proveedor. Selecciona uno de la lista (o créalo en Proveedores).");
      return;
    }

    if (isEdit) {
      setLoading(true);
      try {
        await api.put(`/lotes/${lote.id}`, {
          ...commonPayload(),
          fecha_vencimiento: form.fecha_vencimiento || "",
          costo_unitario: parseNum(form.costo_unitario),
        });
        toast.success("Lote actualizado");
        onSaved();
        onOpenChange(false);
      } catch (err) {
        fail(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Crear compra multiproducto
    const lineas = [];
    for (let idx = 0; idx < lines.length; idx++) {
      const l = lines[idx];
      const pos = lines.length > 1 ? ` (producto ${idx + 1})` : "";
      if (l.nuevo) {
        if (!l.codigo || !l.tipo || !l.nombre) {
          fail(`Completa código, tipo y nombre del producto nuevo${pos}.`);
          return;
        }
      } else if (!l.producto_id) {
        fail(`Selecciona un producto${pos} (o usa "Crear producto nuevo").`);
        return;
      }
      const cant = parseInt(l.cantidad, 10);
      if (!l.cantidad || isNaN(cant) || cant <= 0) {
        fail(`Indica una cantidad mayor a 0${pos}.`);
        return;
      }
      const costo = parseNum(l.costo_unitario);
      if (l.costo_unitario === "" || isNaN(costo) || costo <= 0) {
        fail(`Indica el costo unitario${pos} (mayor a 0).`);
        return;
      }
      lineas.push({
        ...(l.nuevo ? { producto: { codigo: l.codigo, tipo: l.tipo, nombre: l.nombre } } : { producto_id: l.producto_id }),
        cantidad_inicial: cant,
        costo_unitario: costo,
        fecha_vencimiento: l.fecha_vencimiento || null,
      });
    }

    setLoading(true);
    try {
      const { data } = await api.post("/lotes", { ...commonPayload(), lineas });
      if (data.appended) {
        toast.success(`Producto(s) agregado(s) al lote ${data.numero_lote}`);
      } else {
        toast.success(lineas.length > 1 ? `Compra registrada (${lineas.length} productos)` : "Lote creado");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      fail(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const lbl = "text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]";
  const productoFijo = productoId ? productos.find((p) => p.id === productoId) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lote-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
            {isEdit ? "Editar lote" : "Nuevo lote / compra"}
          </DialogTitle>
          <DialogDescription className="text-[#64748B]">
            {isEdit
              ? "Actualiza los datos del lote. El stock se gestiona desde Movimientos."
              : "Una compra puede incluir varios productos. Comparten Nº de lote, proveedor, fecha y factura."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {/* Datos comunes de la compra */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className={lbl}>Número de lote</Label>
              <Input data-testid="lote-field-numero_lote" value={form.numero_lote} onChange={set("numero_lote")} onBlur={(e) => checkCompra(e.target.value)} required className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]" />
            </div>
            <div className="space-y-1.5">
              <Label className={lbl}>Fecha de importación / compra</Label>
              <DateField value={form.fecha_importacion} onChange={(v) => setForm((f) => ({ ...f, fecha_importacion: v }))} testid="lote-field-fecha_importacion" />
            </div>
          </div>

          {!isEdit && compra && (
            <div data-testid="lote-compra-existente" className="rounded-md border border-[#BFDBFE] bg-[#EFF6FF] px-3 py-2.5 text-sm text-[#1E3A8A]">
              Se agregará a la compra <span className="font-bold">{compra.numero_lote}</span> · Proveedor:{" "}
              <span className="font-semibold">{compra.proveedor_nombre || "—"}</span> · Fecha: {fmtFecha(compra.fecha_importacion)} ·{" "}
              {compra.num_productos} producto(s). El proveedor, la moneda y la factura se mantienen de esa compra.
            </div>
          )}

          {!compra && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className={lbl}>Moneda de la compra</Label>
              <Select value={form.moneda} onValueChange={setVal("moneda")}>
                <SelectTrigger data-testid="lote-moneda" className="bg-white border-[#E2E8F0]">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}{c === baseCurrency ? " (base)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {esExtranjera && (
              <div className="space-y-1.5">
                <Label className={lbl}>Tipo de cambio (1 {form.moneda} → {baseCurrency})</Label>
                <Input type="text" inputMode="decimal" data-testid="lote-tipo-cambio" value={form.tipo_cambio} onChange={set("tipo_cambio")} required placeholder="Ej: 950" className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]" />
              </div>
            )}
          </div>
          )}

          {!compra && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className={lbl}>Proveedor</Label>
              {!provInline && (
                <button
                  type="button"
                  data-testid="lote-add-proveedor"
                  onClick={() => setProvInline(true)}
                  className="text-xs font-semibold text-[#0F172A] underline underline-offset-2 hover:text-[#1E293B]"
                >
                  + Nuevo proveedor
                </button>
              )}
            </div>
            {proveedores.length > 0 && (
              <Select key={proveedores.length} value={form.proveedor_id} onValueChange={setVal("proveedor_id")}>
                <SelectTrigger data-testid="lote-proveedor" className="bg-white border-[#E2E8F0]">
                  <SelectValue placeholder="Selecciona un proveedor" />
                </SelectTrigger>
                <SelectContent>
                  {proveedores.map((pr) => (
                    <SelectItem key={pr.id} value={pr.id} data-testid={`lote-proveedor-option-${pr.id}`}>{pr.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {proveedores.length === 0 && !provInline && (
              <p className="text-sm text-[#64748B] bg-[#F8F9FA] border border-[#E2E8F0] rounded-md px-3 py-2">
                No hay proveedores en la agenda. Usa "+ Nuevo proveedor" para agregar uno aquí mismo.
              </p>
            )}
            {provInline && (
              <div className="mt-2 border border-[#E2E8F0] rounded-md p-3 bg-[#F8F9FA] space-y-2" data-testid="proveedor-inline-form">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Nuevo proveedor</p>
                <Input
                  data-testid="nuevo-proveedor-nombre"
                  placeholder="Nombre *"
                  value={nuevoProv.nombre}
                  onChange={(e) => setNuevoProv((p) => ({ ...p, nombre: e.target.value }))}
                  className="bg-white border-[#E2E8F0]"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    data-testid="nuevo-proveedor-email"
                    placeholder="Email"
                    value={nuevoProv.email}
                    onChange={(e) => setNuevoProv((p) => ({ ...p, email: e.target.value }))}
                    className="bg-white border-[#E2E8F0]"
                  />
                  <Input
                    data-testid="nuevo-proveedor-telefono"
                    placeholder="Teléfono"
                    value={nuevoProv.telefono}
                    onChange={(e) => setNuevoProv((p) => ({ ...p, telefono: e.target.value }))}
                    className="bg-white border-[#E2E8F0]"
                  />
                </div>
                <Input
                  data-testid="nuevo-proveedor-condiciones"
                  placeholder="Condiciones de pago (opcional)"
                  value={nuevoProv.condiciones}
                  onChange={(e) => setNuevoProv((p) => ({ ...p, condiciones: e.target.value }))}
                  className="bg-white border-[#E2E8F0]"
                />
                <div className="flex gap-2 pt-1">
                  <Button
                    type="button"
                    size="sm"
                    data-testid="nuevo-proveedor-guardar"
                    onClick={crearProveedor}
                    disabled={savingProv}
                    className="bg-[#0F172A] hover:bg-[#1E293B] text-white font-semibold"
                  >
                    {savingProv ? <CircleNotch size={16} className="animate-spin" /> : "Guardar proveedor"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid="nuevo-proveedor-cancelar"
                    onClick={() => { setProvInline(false); setNuevoProv({ nombre: "", email: "", telefono: "", condiciones: "" }); }}
                    className="border-[#E2E8F0]"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </div>
          )}

          {/* EDICIÓN: línea única */}
          {isEdit && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className={lbl}>Fecha de vencimiento</Label>
                <DateField value={form.fecha_vencimiento} onChange={(v) => setForm((f) => ({ ...f, fecha_vencimiento: v }))} testid="lote-field-fecha_vencimiento" />
              </div>
              <div className="space-y-1.5">
                <Label className={lbl}>Cantidad inicial</Label>
                <Input data-testid="lote-field-cantidad_inicial" type="number" value={form.cantidad_inicial} disabled className="bg-[#F1F5F9] border-[#E2E8F0]" />
              </div>
              <div className="space-y-1.5">
                <Label className={lbl}>Costo unitario ({form.moneda || baseCurrency})</Label>
                <Input data-testid="lote-field-costo_unitario" type="text" inputMode="decimal" value={form.costo_unitario} onChange={set("costo_unitario")} required className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]" />
              </div>
            </div>
          )}

          {/* CREACIÓN: líneas de producto */}
          {!isEdit && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-[#0F172A]">Productos del lote</p>
                {!productoFijo && (
                  <Button type="button" variant="outline" size="sm" onClick={addLine} data-testid="add-linea-button" className="h-8">
                    <Plus size={16} weight="bold" className="mr-1" /> Agregar producto
                  </Button>
                )}
              </div>

              {lines.map((l, i) => (
                <div key={i} data-testid={`lote-linea-${i}`} className="rounded-md border border-[#E2E8F0] p-3 bg-[#F8FAFC] space-y-3">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 space-y-1.5">
                      <Label className={lbl}>Producto</Label>
                      {productoFijo ? (
                        <div className="h-10 flex items-center px-3 rounded-md border border-[#E2E8F0] bg-white text-sm font-medium text-[#0F172A]">
                          {productoFijo.nombre} · {productoFijo.codigo}
                        </div>
                      ) : (
                        <Select value={l.nuevo ? NEW : l.producto_id} onValueChange={(v) => onProductoSelect(i, v)}>
                          <SelectTrigger data-testid={`lote-linea-producto-${i}`} className="bg-white border-[#E2E8F0]">
                            <SelectValue placeholder="Selecciona un producto" />
                          </SelectTrigger>
                          <SelectContent>
                            {productos.map((p) => (
                              <SelectItem key={p.id} value={p.id} data-testid={`lote-linea-option-${p.id}`}>{p.nombre} · {p.codigo}</SelectItem>
                            ))}
                            <SelectItem value={NEW} data-testid="lote-linea-nuevo-producto">➕ Crear producto nuevo</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    {!productoFijo && lines.length > 1 && (
                      <button type="button" onClick={() => removeLine(i)} data-testid={`remove-linea-${i}`} className="mt-6 p-2 rounded-md text-[#991B1B] hover:bg-[#FEF2F2]" title="Quitar">
                        <Trash size={18} />
                      </button>
                    )}
                  </div>

                  {l.nuevo && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <Input data-testid={`linea-nuevo-codigo-${i}`} value={l.codigo} onChange={(e) => setLine(i, "codigo", e.target.value)} placeholder="Código" className="bg-white border-[#E2E8F0]" />
                      <Input data-testid={`linea-nuevo-tipo-${i}`} value={l.tipo} onChange={(e) => setLine(i, "tipo", e.target.value)} placeholder="Tipo" className="bg-white border-[#E2E8F0]" />
                      <Input data-testid={`linea-nuevo-nombre-${i}`} value={l.nombre} onChange={(e) => setLine(i, "nombre", e.target.value)} placeholder="Nombre" className="bg-white border-[#E2E8F0]" />
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div className="space-y-1.5">
                      <Label className={lbl}>Cantidad</Label>
                      <Input data-testid={`lote-linea-cantidad-${i}`} type="number" min="1" value={l.cantidad} onChange={(e) => setLine(i, "cantidad", e.target.value)} placeholder="0" className="bg-white border-[#E2E8F0]" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className={lbl}>Costo unit. ({form.moneda || baseCurrency})</Label>
                      <Input data-testid={`lote-linea-costo-${i}`} type="text" inputMode="decimal" value={l.costo_unitario} onChange={(e) => setLine(i, "costo_unitario", e.target.value)} placeholder="0,00" className="bg-white border-[#E2E8F0]" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className={lbl}>Vencimiento</Label>
                      <DateField value={l.fecha_vencimiento} onChange={(v) => setLine(i, "fecha_vencimiento", v)} testid={`lote-linea-vencimiento-${i}`} />
                    </div>
                  </div>
                </div>
              ))}
              <p className="text-xs text-[#94A3B8]">El costo por embalaje se calcula automáticamente: costo unitario × unidades por embalaje del producto.</p>
            </div>
          )}

          {/* Datos de factura (de la compra completa) */}
          {!compra && (
          <div className="pt-2 border-t border-[#E2E8F0] space-y-4">
            <p className="text-sm font-bold text-[#0F172A]">Datos de factura {!isEdit && "(de la compra completa)"}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className={lbl}>Nº de factura</Label>
                <Input data-testid="lote-numero-factura" value={form.numero_factura} onChange={set("numero_factura")} placeholder="F-12345" className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]" />
              </div>
              <div className="space-y-1.5">
                <Label className={lbl}>Vencimiento factura</Label>
                <DateField value={form.factura_vencimiento} onChange={(v) => setForm((f) => ({ ...f, factura_vencimiento: v }))} testid="lote-factura-vencimiento" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className={lbl}>Monto neto</Label>
                <Input type="text" inputMode="decimal" data-testid="lote-factura-neto" value={form.factura_neto} onChange={onNetoChange} placeholder="0" className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]" />
              </div>
              <div className="space-y-1.5">
                <Label className={lbl}>IVA (19%)</Label>
                <Input type="text" inputMode="decimal" data-testid="lote-factura-iva" value={form.factura_iva} onChange={set("factura_iva")} placeholder="0" className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]" />
              </div>
              <div className="space-y-1.5">
                <Label className={lbl}>Monto bruto</Label>
                <div data-testid="lote-factura-bruto" className="h-10 flex items-center px-3 rounded-md border border-[#E2E8F0] bg-[#F8F9FA] text-sm font-semibold text-[#0F172A]">
                  {fmtMoney(fBruto, form.moneda || baseCurrency)}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className={lbl}>Estado de pago</Label>
                <Select value={form.estado_pago} onValueChange={setVal("estado_pago")}>
                  <SelectTrigger data-testid="lote-estado-pago" className="bg-white border-[#E2E8F0]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="pagado">Pagado</SelectItem>
                    <SelectItem value="abono">Abono parcial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.estado_pago === "abono" && (
                <div className="space-y-1.5">
                  <Label className={lbl}>Monto abonado</Label>
                  <Input type="text" inputMode="decimal" data-testid="lote-monto-abonado" value={form.monto_abonado} onChange={set("monto_abonado")} placeholder="0" className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]" />
                </div>
              )}
            </div>
            {form.estado_pago === "abono" && (fBruto > 0 || fAbonado > 0) && (
              <div data-testid="lote-saldo-preview" className="rounded-md border border-[#FED7AA] bg-[#FFF7ED] px-3 py-2.5 text-sm text-[#9A3412]">
                <span className="font-semibold">Abonado:</span> {fmtMoney(fAbonado, form.moneda || baseCurrency)} ·{" "}
                <span className="font-semibold">Saldo pendiente:</span> {fmtMoney(fSaldo, form.moneda || baseCurrency)}
              </div>
            )}
          </div>
          )}

          {isEdit && (
            <p className="text-xs text-[#64748B] bg-[#F1F5F9] rounded-md px-3 py-2">
              La cantidad inicial no se edita aquí. Usa Movimientos para registrar ingresos o salidas.
            </p>
          )}

          {error && (
            <p data-testid="lote-form-error" className="text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-md px-3 py-2">{error}</p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="lote-form-cancel">Cancelar</Button>
            <Button type="submit" disabled={loading} data-testid="lote-form-submit" className="bg-[#0F172A] hover:bg-[#1E293B] text-white">
              {loading ? <CircleNotch size={18} className="animate-spin" /> : isEdit ? "Guardar cambios" : "Crear lote"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
